import React from 'react'
import DOMPurify from 'dompurify'
import { CENSUS_ID_1940, IGI_ID } from './collectionConstants'

/**
 * Gets the full-text document count from sourceDescription.fields (DocumentCount field).
 * This is the only place full-text collections expose a record count — their API responses
 * never populate collection.searchMetadata.
 * @param {object} sourceDescription - The source description object
 * @returns {number|undefined} The document count, or undefined if missing/non-numeric
 */
export function getFullTextDocumentCount(sourceDescription) {
  const documentCountText = sourceDescription?.fields?.find?.(
    (field) => field?.type === 'http://familysearch.org/types/fields/DocumentCount'
  )?.values?.[0]?.text
  if (!documentCountText) return undefined
  const parsed = Number(documentCountText)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Determines if a collection has no search form (no records, only images)
 * @param {object} collection - The collection object
 * @param {string} dataType - The type of collection (e.g. 'full-text', 'hr')
 * @param {object} sourceDescription - The source description object (used for full-text record counts)
 * @returns {boolean} True if the collection has no searchable records
 */
export function hasNoSearchForm(collection, dataType, sourceDescription) {
  if (dataType === 'full-text') {
    const documentCount = getFullTextDocumentCount(sourceDescription)
    return documentCount !== undefined && documentCount <= 0
  }
  return collection?.searchMetadata?.[0]?.recordCount <= 0
}

/**
 * Gets the collection ID from the URL pathname, handling special routes like 1940census and igi
 * @param {string} pathname - The URL pathname
 * @param {string} collectionId - The collection ID from params
 * @returns {string} The resolved collection ID
 */
export function getCollectionIdFromPathname(pathname, collectionId) {
  if (pathname.includes('/1940census')) {
    return CENSUS_ID_1940
  }
  if (collectionId === 'igi') {
    return IGI_ID
  }
  return collectionId
}

/**
 * Sanitizes IGI collection description HTML, allowing links
 * @param {string} description - The collection description HTML
 * @returns {JSX.Element} A span element with sanitized HTML
 */
export function getSanitizedCollectionDescription(description) {
  if (!description) return null

  const sanitizedHtml = DOMPurify.sanitize(description, {
    ALLOWED_TAGS: ['a', 'i'],
    ALLOWED_ATTR: ['href'],
  })

  return (
    <span
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  )
}
